import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { Factory, ChevronDown, ChevronRight, FileText } from "lucide-react";

/**
 * FactoryStatusPanel — Feb 2026
 * CRM-facing card on the Design Project detail page. Shows the latest
 * factory-side status updates (Production Manager, Procurement Manager,
 * Purchase Manager, Purchase Executive) and the 4 handoff files.
 * Silently hides when the caller lacks access (403) or the order hasn't
 * reached Sent-to-Factory yet.
 */
export default function FactoryStatusPanel({ projectId }) {
  const [state, setState] = useState({ loading: true, data: null, hidden: false });
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/crm/projects/${projectId}/factory-status`);
      // Hide when there's no factory order yet
      if (!data.order_id) return setState({ loading: false, data: null, hidden: true });
      setState({ loading: false, data, hidden: false });
    } catch (err) {
      if (err?.response?.status === 403) setState({ loading: false, data: null, hidden: true });
      else setState({ loading: false, data: null, hidden: false, error: formatApiErrorDetail(err?.response?.data?.detail) });
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000); return () => clearInterval(t);
  }, [load]);

  if (state.hidden) return null;
  if (state.loading) return (
    <div className="iz-card p-5"><p className="text-xs text-gray-500">Loading factory status…</p></div>
  );
  if (!state.data) return null;

  const { updates, factory_files, order_status } = state.data;
  const slots = [
    { key: "two_d", label: "2D" },
    { key: "three_d", label: "3D" },
    { key: "ppt", label: "PPT" },
    { key: "quotation", label: "Quotation" },
  ];

  return (
    <div className="iz-card p-5" data-testid="crm-factory-status-panel">
      <button
        onClick={() => setExpanded((x) => !x)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            Factory Status
          </p>
          {order_status === "sent_to_factory" && (
            <span className="text-[9px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-0.5 rounded">
              Live
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Handoff Files</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {slots.map((s) => {
                const f = factory_files?.[s.key];
                return (
                  <div key={s.key} className="border border-[#262626] rounded px-3 py-2" data-testid={`crm-factory-file-${s.key}`}>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{s.label}</p>
                    {f ? (
                      <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                          className="text-xs text-[#d4af37] flex items-center gap-1 mt-1 truncate">
                        <FileText className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                        <span className="truncate">Open</span>
                      </a>
                    ) : (
                      <p className="text-[11px] text-gray-600 italic mt-1">—</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
              Updates ({updates.length})
            </p>
            {updates.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No updates yet from factory teams.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {updates.map((u) => (
                  <div key={u.id} className="border border-[#262626] rounded px-3 py-2" data-testid={`crm-factory-update-${u.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{u.role}</p>
                      <p className="text-[10px] text-gray-600">{new Date(u.at).toLocaleString()}</p>
                    </div>
                    <p className="text-sm text-gray-200 mt-1">{u.status}</p>
                    {u.note && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{u.note}</p>}
                    <p className="text-[10px] font-mono text-gray-600 mt-1">by {u.by_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
